import React, { useState, useEffect, Fragment, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, MenuItem } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { FormControl } from "@mui/material";
import { TextField } from "@mui/material";
import { Select } from "@mui/material";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import RichTextBoxTabPanel from "../../../components/RichTextBoxEditerTabs";
import {
  createIncidentResponsePlan,
  updateIncidentResponsePlan,
  createRevisionHistory,
} from "../helper";
import { useIncidentResponsePlanContext } from "../../../context/IncidentResponsePlan/IncidentPlanResponceContext";
import { RevisionHistory } from "../components/RevisionHistory";
import { convertToUTC, isClientAdmin } from "../../../utils/ConvertDate";
import { useUserContext } from "../../../context/auth/UserContext";
import AutoVersionIncrement from "../../../components/AutoVersionIncrement";
import { useLocation } from "react-router-dom";
import { useAccessRights } from "../../../utils/useAccessRights";

const initialFormState = {
  overview: "",
  purpose: "",
  scope: "",
  definition: "",
  incidentResponseTeam: "",
  preparation: "",
  detection: "",
  analysis: "",
  containment: "",
  eradication: "",
  recovery: "",
  reporting: "",
  compliance: "",
  relatedPolicies: "",
  version: "",
  originalVersion: "",
  versionIncrement: "",
  dateApproved: null,
  status: "",
};

export const IncidentResponsePlanForm = () => {
  const incidentResponsePlanAccess = useAccessRights("Incident Response Plan");

  const navigate = useNavigate();
  const location = useLocation();
  const [planFormTitle, setPlanFormTitle] = useState<string>(
    "Create Incident Response Plan"
  );
  const [isUpdated, setIsUpdated] = useState(false);
  const isUpdatedRef = useRef(isUpdated);
  const [openRevisionHistory, setOpenRevisionHistory] = useState(false);
  const [formValues, setFormValues] = useState(initialFormState);
  const [isUserStillOnPage, setIsUserStillOnPage] = useState(false);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openSuccessDialogBox: false,
    openEditConfirmationDialog: false,
  });
  const { userId, companyProfileID, userAccess } = useUserContext();
  const { selectedIncidentResponsePlan } = useIncidentResponsePlanContext();

  const tabDetalis = [
    {
      label: "overview",
      key: "overview",
      id: `tab-overview`,
      editerValue: formValues.overview,
    },
    {
      label: "purpose",
      key: "purpose",
      id: `tab-purpose`,
      editerValue: formValues.purpose,
    },
    {
      label: "scope",
      key: "scope",
      id: `tab-scope`,
      editerValue: formValues.scope,
    },
    {
      label: "definition",
      key: "definition",
      id: `tab-definition`,
      editerValue: formValues.definition,
    },
    {
      label: "Incident Response Team",
      key: "incidentResponseTeam",
      id: `tab-incidentResponseTeam`,
      editerValue: formValues.incidentResponseTeam,
    },

    {
      label: "preparation",
      key: "preparation",
      id: `tab-preparation`,
      editerValue: formValues.preparation,
    },
    {
      label: "detection",
      key: "detection",
      id: `tab-detection`,
      editerValue: formValues.detection,
    },
    {
      label: "analysis",
      key: "analysis",
      id: `tab-analysis`,
      editerValue: formValues.analysis,
    },
    {
      label: "containment",
      key: "containment",
      id: `tab-containment`,
      editerValue: formValues.containment,
    },
    {
      label: "eradication",
      key: "eradication",
      id: `tab-eradication`,
      editerValue: formValues.eradication,
    },
    {
      label: "recovery",
      key: "recovery",
      id: `tab-recovery`,
      editerValue: formValues.recovery,
    },
    {
      label: "reporting",
      key: "reporting",
      id: `tab-reporting`,
      editerValue: formValues.reporting,
    },
    {
      label: "compliance",
      key: "compliance",
      id: `tab-compliance`,
      editerValue: formValues.compliance,
    },
    {
      label: "Related Policies",
      key: "relatedPolicies",
      id: `tab-relatedPolicies`,
      editerValue: formValues.relatedPolicies,
    },
  ];

  const resetPage = () => {
    navigate("/incident-response-plan");
    setFormValues(initialFormState);
  };

  const handleCancel = async () => {
    resetPage();
  };

  const handleCloseDialog = () => {
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
      openEditConfirmationDialog: false,
    });
    resetPage();
  };

  const handleCloseEditDialog = () => {
    setFormValues((prevVal) => ({ ...prevVal, status: "Approved" }));
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
      openEditConfirmationDialog: false,
    });
  };

  const handleTabEditerChange = (val: any, key: any) => {
    handleFormChange({
      target: { value: val, id: key },
    });
  };

  const handleFormChange = (e: { target: { id: any; value: any } }) => {
    const { id, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [id]: value,
    }));
  };

  const disableVersionChange = () => {
    if (
      selectedIncidentResponsePlan[0]?.incidentResponseInfoPlanID &&
      selectedIncidentResponsePlan[0]?.status !== formValues.status &&
      formValues.status === "Approved"
    ) {
      return false;
    }
    return true;
  };

  const disableForm = () => {
    if (
      formValues.version === "" ||
      formValues.status === "" ||
      formValues.overview === "" ||
      formValues.purpose === "" ||
      formValues.scope === "" ||
      formValues.definition === "" ||
      formValues.compliance === "" ||
      formValues.incidentResponseTeam === "" ||
      formValues.preparation === "" ||
      formValues.detection === "" ||
      formValues.analysis === "" ||
      formValues.containment === "" ||
      formValues.eradication === "" ||
      formValues.recovery === "" ||
      formValues.reporting === "" ||
      formValues.relatedPolicies === ""
    ) {
      return true;
    }

    if (formValues.status === "Approved") {
      if (
        !formValues.dateApproved ||
        (!disableVersionChange() && formValues.versionIncrement === "")
      ) {
        return true;
      }
    }

    return false;
  };

  const handleAddPlan = async () => {
    const date = new Date();
    const approvalDate = formValues?.dateApproved && formValues.dateApproved;

    const payload = {
      overview: formValues.overview,
      purpose: formValues.purpose,
      scope: formValues.scope,
      definition: formValues.definition,
      compliance: formValues.compliance,
      incidentResponseTeam: formValues.incidentResponseTeam,
      preparation: formValues.preparation,
      detection: formValues.detection,
      analysis: formValues.analysis,
      containment: formValues.containment,
      eradication: formValues.eradication,
      recovery: formValues.recovery,
      reporting: formValues.reporting,
      relatedPolicies: formValues.relatedPolicies,
      version: formValues.version,
      dateApproved: convertToUTC(approvalDate),
      status: formValues.status,
      createDate: date,
      createdBy: userId,
      companyProfileID: companyProfileID,
      approvedBy: formValues.status === "Approved" ? userId : 0,
      isEditedByUserId: "0",
    };
    // console.log("handleCreatePlan payload",payload)
    const res = await createIncidentResponsePlan(payload);
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: "Plan successfully created",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    }
  };

  const handleEditPlan = async () => {
    setIsUpdated(true);
    const date = new Date();
    const payload = {
      overview: formValues.overview,
      purpose: formValues.purpose,
      scope: formValues.scope,
      definition: formValues.definition,
      compliance: formValues.compliance,
      incidentResponseTeam: formValues.incidentResponseTeam,
      preparation: formValues.preparation,
      detection: formValues.detection,
      analysis: formValues.analysis,
      containment: formValues.containment,
      eradication: formValues.eradication,
      recovery: formValues.recovery,
      reporting: formValues.reporting,
      relatedPolicies: formValues.relatedPolicies,
      version: formValues.version,
      dateApproved: convertToUTC(formValues.dateApproved),
      status: formValues.status,
      updateDate: date,
      updateBy: userId,
      companyProfileID: companyProfileID,
      approvedBy: formValues.status === "Approved" ? userId : 0,
      incidentResponseInfoPlanID:
        selectedIncidentResponsePlan[0].incidentResponseInfoPlanID,
      isEditedByUser: false,
      isEditedByUserId: "0",
    };
    const res = await updateIncidentResponsePlan(payload);
    if (res.succeeded === true) {
      handleRevisionHistoryCancel();
      setDialogState({
        title: "Success",
        body: "Plan successfully updated",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    }
  };

  const handleRevisionHistoryCancel = () => {
    setOpenRevisionHistory(false);
  };

  const handleRevisionHistoryCreate = async (val: any) => {
    const date = new Date();
    const approvalDate = formValues?.dateApproved
      ? dayjs.isDayjs(formValues.dateApproved)
        ? formValues.dateApproved
        : dayjs(formValues.dateApproved).isValid()
        ? dayjs(formValues.dateApproved).toDate()
        : null
      : null;
    const payload = {
      descriptionOfChanges: val,
      comments: val,
      status: formValues.status,
      dateApproved: approvalDate,
      version: formValues.version,
      createDate: date,
      createdBy: userId,
      approvedBy: userId,
      incidentResponseInfoPlanID:
        selectedIncidentResponsePlan[0].incidentResponseInfoPlanID,
    };
    const res = await createRevisionHistory(payload);
    if (res.succeeded === true) {
      handleEditPlan();
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
        openEditConfirmationDialog: false,
      });
    }
  };

  const handleEditRevision = () => {
    if (
      (formValues.versionIncrement === "Major" &&
        formValues.version !== selectedIncidentResponsePlan[0].version) ||
      (formValues.status === "Approved" &&
        formValues.status !== selectedIncidentResponsePlan[0].status)
    ) {
      setOpenRevisionHistory(true);
    } else if (
      selectedIncidentResponsePlan[0].status === "Approved" &&
      selectedIncidentResponsePlan[0].status === formValues.status
    ) {
      setDialogState({
        title: "Confirmation",
        body: "Editing an Approved record will change the status to Ready to Approve . Do you want to continue?",
        action: "OK",
        actionCancel: "Cancel",
        openSuccessDialogBox: false,
        openEditConfirmationDialog: true,
      });
      setFormValues((prevVal) => ({ ...prevVal, status: "Ready to Approve" }));
    } else {
      handleEditPlan();
    }
  };

  useEffect(() => {
    if (formValues.status !== "Approved") {
      setFormValues((prevVal) => ({
        ...prevVal,
        versionIncrement: "",
        version: formValues.originalVersion,
        dateApproved: null,
      }));
    }
  }, [formValues.status]);

  console.log("onIncrementChange", formValues);

  useEffect(() => {
    if (selectedIncidentResponsePlan[0]?.incidentResponseInfoPlanID) {
      const dateApproved = selectedIncidentResponsePlan[0].dateApproved
        ? dayjs(selectedIncidentResponsePlan[0].dateApproved)
        : null;
      setFormValues({
        overview: selectedIncidentResponsePlan[0].overview,
        purpose: selectedIncidentResponsePlan[0].purpose,
        scope: selectedIncidentResponsePlan[0].scope,
        definition: selectedIncidentResponsePlan[0].definition,
        incidentResponseTeam:
          selectedIncidentResponsePlan[0].incidentResponseTeam,
        compliance: selectedIncidentResponsePlan[0].compliance,
        preparation: selectedIncidentResponsePlan[0].preparation,
        detection: selectedIncidentResponsePlan[0].detection,
        analysis: selectedIncidentResponsePlan[0].analysis,
        containment: selectedIncidentResponsePlan[0].containment,
        eradication: selectedIncidentResponsePlan[0].eradication,
        recovery: selectedIncidentResponsePlan[0].recovery,
        reporting: selectedIncidentResponsePlan[0].reporting,
        relatedPolicies: selectedIncidentResponsePlan[0].relatedPolicies,
        version: selectedIncidentResponsePlan[0].version,
        originalVersion: selectedIncidentResponsePlan[0].version,
        versionIncrement: "",
        dateApproved: dateApproved,
        status: selectedIncidentResponsePlan[0].status,
      });

      setPlanFormTitle("Edit Incident Response Plan");
    } else {
      setFormValues((prevValues) => ({
        ...prevValues,
        status: "Not Approved",
        version: "1.0",
        originalVersion: "1.0",
      }));
      setPlanFormTitle("Create Incident Response Plan");
    }
  }, [selectedIncidentResponsePlan]);

  useEffect(() => {
    console.log("isUpdated", isUpdated);
    isUpdatedRef.current = isUpdated;
  }, [isUpdated]);

  const handleExit = async (isEdited: boolean) => {
    console.log("handleExit", isUpdatedRef.current);
    if (
      selectedIncidentResponsePlan[0]?.incidentResponseInfoPlanID &&
      !isUpdatedRef.current
    ) {
      const date = new Date();
      const payload = {
        ...selectedIncidentResponsePlan[0],
        isEditedByUser: isEdited,
        isEditedByUserId: userId + "",
        updateBy: userId,
        updateDate: date,
      };
      try {
        const resUpdate = await updateIncidentResponsePlan(payload);
        console.log("handleExit resUpdate", resUpdate);
        if (resUpdate) {
          setIsUserStillOnPage(false);
          return resUpdate;
        }
      } catch (error) {
        console.log("@@Error", error);
      }
    }
  };

  useEffect(() => {
    return () => {
      handleExit(false);
    };
  }, [location]);

  useEffect(() => {
    console.log("isUserStillOnPage >> ", isUserStillOnPage);
    if (isUserStillOnPage) {
      handleExit(true);
    }
  }, [isUserStillOnPage]);

  useEffect(() => {
    console.log("beforeunload");

    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      console.log("handleBeforeUnload", event);
      event.preventDefault();
      await handleExit(false);
      setIsUserStillOnPage(true);
      const message =
        "You have unsaved changes. Are you sure you want to leave? ";
      event.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <Fragment>
      <Grid
        container
        justifyContent="center"
        sx={{
          height: "100%",
          width: "100%",
          padding: "1rem",
          margin: "1rem 0",
          background: "#FFF",
          borderRadius: "2%",
        }}
      >
        <Grid xs={12}>
          <h3 style={{ fontWeight: "400", margin: "0.5rem 0" }}>
            {planFormTitle}
          </h3>
        </Grid>

        <Grid container spacing={2} sx={{ height: "100%", width: "95%" }}>
          <Grid xs={12}>
            <RichTextBoxTabPanel
              readonly={false}
              tabs={tabDetalis}
              handleTabEditerChange={handleTabEditerChange}
            />
          </Grid>
          <AutoVersionIncrement
            disableIncrement={disableVersionChange()}
            version={formValues.version}
            originalVersion={formValues.originalVersion}
            versionIncrement={formValues.versionIncrement}
            onChange={(version, versionIncrement) =>
              setFormValues({
                ...formValues,
                version,
                versionIncrement,
              })
            }
          />
          <Grid xs={6}>
            <h4
              style={{
                color: "lightgrey",
                fontWeight: "400",
                fontStyle: "italic",
                margin: "0.5rem 0",
              }}
            >
              Status
            </h4>
            <FormControl variant="filled" sx={{ minWidth: 200, width: "100%" }}>
              <Select
                id="status"
                value={formValues.status}
                onChange={(e) =>
                  handleFormChange({
                    target: { value: e.target.value, id: "status" },
                  })
                }
              >
                <MenuItem value="Not Approved">Not Approved</MenuItem>
                <MenuItem value="Ready to Approve">Ready to Approve</MenuItem>
                <MenuItem
                  value="Approved"
                  disabled={!incidentResponsePlanAccess?.isApproved}
                >
                  Approved
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={6}>
            <h4
              style={{
                color: "lightgrey",
                fontWeight: "400",
                fontStyle: "italic",
                margin: "0.5rem 0",
              }}
            >
              Approval Date
            </h4>
            <DatePicker
              slotProps={{ field: { clearable: true } }}
              value={formValues.dateApproved}
              // onChange={(val) => {
              //   if (val) {
              //     const selectedDate = dayjs(val); // Convert the selected date to a Day.js object
              //     handleFormChange({
              //       target: {
              //         id: "dateApproved",
              //         value: selectedDate, // Set the selected date as a Day.js object
              //       },
              //     });
              //   } else {
              //     // Handle the case where the date is cleared
              //     handleFormChange({
              //       target: {
              //         id: "dateApproved",
              //         value: null, // or some default value
              //       },
              //     });
              //   }
              // }}
              onChange={(val) =>
                handleFormChange({
                  target: {
                    id: "dateApproved",
                    value: val,
                  },
                })
              }
            />
          </Grid>
          <Grid xs={12} container justifyContent="end">
            <Button
              sx={{ p: 1, m: 1 }}
              variant="outlined"
              size="small"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            {selectedIncidentResponsePlan[0]?.incidentResponseInfoPlanID ? (
              <Button
                sx={{ p: 1, m: 1 }}
                variant="contained"
                size="small"
                disabled={
                  disableForm() || !incidentResponsePlanAccess?.isUpdate
                }
                onClick={handleEditRevision}
              >
                Edit
              </Button>
            ) : (
              <Button
                sx={{ p: 1, m: 1 }}
                variant="contained"
                size="small"
                disabled={
                  disableForm() || !incidentResponsePlanAccess?.isCreate
                }
                onClick={handleAddPlan}
              >
                Create
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      {
        <RevisionHistory
          openDialogBox={openRevisionHistory}
          handleCancel={handleRevisionHistoryCancel}
          handleCreate={(val: any) => handleRevisionHistoryCreate(val)}
        />
      }
      {
        // Edit confirmation
        <DialogBoxComponent
          openDialogBox={dialogState.openEditConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleEditPlan}
          handleCancel={handleCloseEditDialog}
        />
      }
      {
        //Success
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
